import { atom } from 'jotai';

import { organizationCreateMutationAtom } from '../_graphql/mutationOrganizationCreate';
import { organizationResetMutationAtom } from '../_graphql/mutationOrganizationReset';
import { organizationSetOnboardedMutationAtom } from '../_graphql/mutationOrganizationSetOnboarded';
import { organizationUpdateMutationAtom } from '../_graphql/mutationOrganizationUpdate';

import { $organizations } from './organizationList.state';

// 🔘🔘🔘 TYPES
export type OrganizationDialogMode = 'create' | 'edit';

export type OrganizationFormData = {
  name: string;
  domain: string;
  timezone?: string;
  idpId?: string;
};

// 🔘🔘🔘 INITIAL STATE
const initialFormData: OrganizationFormData = {
  name: '',
  domain: '',
  timezone: undefined,
  idpId: undefined,
};

// 🔘🔘🔘 PLAIN ATOMS
export const $organizationDialogOpen = atom(false);
export const $organizationDialogMode = atom<OrganizationDialogMode>('create');
export const $organizationDialogId = atom<string | null>(null);
export const $organizationFormData =
  atom<OrganizationFormData>(initialFormData);

// 🟡🟡🟡 HIDDEN ACTION -> CREATE organization
export const $createOrganization = organizationCreateMutationAtom({
  onSuccess: (get, set) => {
    get($organizations).refetch();
    set($organizationDialogOpen, false);
    set($organizationFormData, initialFormData);
  },
});

// 🟡🟡🟡 HIDDEN ACTION -> UPDATE organization
export const $updateOrganization = organizationUpdateMutationAtom({
  onSuccess: (get, set) => {
    get($organizations).refetch();
    set($organizationDialogOpen, false);
    set($organizationFormData, initialFormData);
  },
});

// 🟠🟠🟠 ACTION -> OPEN dialog for create
export const $openCreateOrganizationDialog = atom(null, (_get, set) => {
  set($organizationDialogMode, 'create');
  set($organizationDialogId, null);
  set($organizationFormData, initialFormData);
  set($organizationDialogOpen, true);
});

// 🟠🟠🟠 ACTION -> OPEN dialog for edit
export const $openEditOrganizationDialog = atom(
  null,
  (
    _get,
    set,
    organization: {
      id: string;
      name: string;
      domain: string;
      timezone?: string | null;
      idpId?: string | null;
    },
  ) => {
    set($organizationDialogMode, 'edit');
    set($organizationDialogId, organization.id);
    set($organizationFormData, {
      name: organization.name,
      domain: organization.domain,
      timezone: organization.timezone ?? undefined,
      idpId: organization.idpId ?? undefined,
    });
    set($organizationDialogOpen, true);
  },
);

// 🟠🟠🟠 ACTION -> CLOSE dialog
export const $closeOrganizationDialog = atom(null, (_get, set) => {
  set($organizationDialogOpen, false);
  set($organizationFormData, initialFormData);
  set($organizationDialogId, null);
});

// 🟠🟠🟠 ACTION -> UPDATE form data
export const $updateOrganizationFormData = atom(
  null,
  (get, set, update: Partial<OrganizationFormData>) => {
    const currentData = get($organizationFormData);
    set($organizationFormData, {
      ...currentData,
      ...update,
    });
  },
);

// 🟠🟠🟠 ACTION -> SAVE organization
export const $saveOrganization = atom(null, async (get, set) => {
  const mode = get($organizationDialogMode);
  const formData = get($organizationFormData);
  const organizationId = get($organizationDialogId);

  if (mode === 'edit' && organizationId) {
    await set($updateOrganization, {
      id: organizationId,
      ...formData,
    });
  } else {
    await set($createOrganization, formData);
  }
});

// 🟢🟢🟢 COMPUTED DATA -> form validation
export const $organizationFormValid = atom((get) => {
  const formData = get($organizationFormData);
  return !!(formData.name.trim() && formData.domain.trim());
});

// 🟢🟢🟢 COMPUTED DATA -> is saving
export const $organizationDialogSaving = atom((get) => {
  const createStatus = get($createOrganization);
  const updateStatus = get($updateOrganization);
  return createStatus.isPending || updateStatus.isPending;
});

// 🟡🟡🟡 HIDDEN ACTION -> SET organization onboarded status
export const $setOrganizationOnboarded = organizationSetOnboardedMutationAtom({
  onSuccess: (get) => {
    get($organizations).refetch();
  },
});

// 🟠🟠🟠 ACTION -> SET organization onboarded status
export const $setOrganizationOnboardedStatus = atom(
  null,
  async (_get, set, params: { organizationId: string; onboarded: boolean }) => {
    await set($setOrganizationOnboarded, {
      id: params.organizationId,
      onboarded: params.onboarded,
    });
  },
);

// 🔘🔘🔘 PLAIN ATOMS
export const $resetOrganizationDialogOpen = atom(false);
export const $resetOrganizationTarget = atom<{
  id: string;
  name: string;
} | null>(null);

// 🟡🟡🟡 HIDDEN ACTION -> RESET organization
export const $resetOrganization = organizationResetMutationAtom({
  onSuccess: (get, set) => {
    get($organizations).refetch();
    set($resetOrganizationDialogOpen, false);
    set($resetOrganizationTarget, null);
  },
});

// 🟠🟠🟠 ACTION -> OPEN reset dialog
export const $openResetOrganizationDialog = atom(
  null,
  (_get, set, organization: { id: string; name: string }) => {
    set($resetOrganizationTarget, organization);
    set($resetOrganizationDialogOpen, true);
  },
);

// 🟠🟠🟠 ACTION -> CLOSE reset dialog
export const $closeResetOrganizationDialog = atom(null, (_get, set) => {
  set($resetOrganizationDialogOpen, false);
  set($resetOrganizationTarget, null);
});
